import { inject, injectable } from 'inversify';
import { makeObservable, observable } from 'mobx';
import { TYPES } from '../ioc/types';
import type { UserPreferencesPersistenceApi } from './UserPreferencesPersistenceApi';

@injectable()
export class UserPreferencesStore<T> {
  @observable
  private _store: T | undefined;

  private readonly _userPreferencesApi: UserPreferencesPersistenceApi;

  private _type: string | undefined;

  private _debug = false;

  public constructor(@inject(TYPES.UserPreferencesPersistenceApi) userPreferencesApi: UserPreferencesPersistenceApi) {
    makeObservable(this);

    this._userPreferencesApi = userPreferencesApi;
  }

  private _logInfo(message: string): void {
    if (this._debug === true) console.info(message);
  }

  public async init(initialStore: T | undefined, type?: string): Promise<void> {
    this._type = type;

    if (this._type === undefined) {
      throw Error('type should be defined');
    }

    let apiSavedStore;
    try {
      apiSavedStore = await this._userPreferencesApi.get<T>(this._type);
    } catch (err: unknown) {
      apiSavedStore = undefined;

      console.error('failed to get user preferences from api', (err as Error).message);
    }

    if (apiSavedStore && Object.keys(apiSavedStore).length > 0) {
      this._logInfo('user preferences loaded from backend');

      this._store = apiSavedStore;
      return;
    }

    this._logInfo('user preferences not found, using default');
    this._store = initialStore;
  }

  public set(store: T | undefined): void {
    if (this._type === undefined) {
      throw Error('type should be defined');
    }

    this._store = store;
    this._userPreferencesApi.save(this._type, store);
  }

  public get<K extends keyof T>(key: K): T[K] {
    if (this._store === undefined || this._store === null) {
      throw Error('_store should be defined');
    }
    return this._store[key];
  }

  public getStore(): T {
    if (this._store === undefined || this._store === null) {
      throw Error('_store should be defined');
    }
    return this._store;
  }
}
